// RraFormPage — PUBLIC requester accommodation-request form (/rra/:token), RRA v2.1.
// Modeled on IntakeFormPage/VerificationFormPage: token-gated, no auth shell, state-aware (never
// dead-ends). The API re-gates every read/write against the live case, so this page just renders
// what GET returns. Two token purposes land here:
//   • the requester's own link  — the form, the confirmation page, and (when staff send one) the
//                                 one-screen "who can confirm?" permission page (A6);
//   • an adult household member's own link — only that permission page (B2): only the patient can
//                                 give permission to contact someone about a competent adult.
//
// THE FORM IS SHAPED BY THE GATE (A1). `view.shape` says which sections exist: "Your disability"
// only when disability is in the gap, the optional help box + the need affirmation only when need
// is, "Confirming your request" only when either is, and confirm-and-send when neither is. Every
// word is from lib/rraCopy.ts (byte-identical with the API's copy); a handful of form-UI lines the
// consensus gives no wording for live in lib/rraRequestDraft.ts (FORM_UI, DRAFT).
//
// RRA v2.2 (WEL-180): requests, not animals. The form asks for NO animal details — an animal's name,
// kind and breed are a post-approval matter (the lease intake, or the household animal form). A case
// carries several requests; each is titled "Request 1", "Request 2". The form renders whatever
// `view.items` / `view.shape` say, so a form reopened for a newly added request shows only that
// request (and only the sections the server says are still needed).
//
// PRIVACY BY DESIGN (docs/design/rra-workflow.md): the form collects ONLY what the schema can hold —
// WHAT the requester is asking us to change, one optional ≤300-character help
// box that says (twice) to leave out the disability, symptoms and treatment, the two permissible
// service-animal questions, the Washington definition + one attestation, a typed-name signature,
// and EITHER named verifier contacts (with counsel's permission text printed word for word) OR an
// uploaded existing letter (quarantined server-side). There is no field to put a diagnosis in.
//
// B10: the submit button is ALWAYS enabled. Pressing it with something missing shows "Almost there.
// Still needed:" and a linked list naming each missing item by its label; focus moves to the list
// and each field is marked. A server refusal renders beside the button and takes focus. Every input
// has a <label htmlFor>; every radio/checkbox group is a <fieldset><legend> with a shared name.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  getRraForm,
  rraRequestPdfUrl,
  submitRraForm,
  submitRraPermission,
  uploadRraLetter,
  RraFormApiError,
  type RraFormView,
} from '@/lib/rraFormClient';

// The office line — one shared constant (WEL-171: 509-525-1040 everywhere).
import { OFFICE_PHONE } from '@/lib/officeContact';
import {
  ADD_PERSON,
  AFFIRMATION_HEADING,
  CONFIRM_CHOICE,
  CONFIRM_HEADING,
  CONFIRM_ONLY_QUESTION,
  DOWNLOAD_CONFIRMED_COPY,
  DOWNLOAD_SIGNED_COPY,
  FORM_PRIVACY_LINE,
  LETTER_DIAGNOSIS_OK,
  LETTER_UPLOAD_LABEL,
  PERMISSION_PAGE,
  REQUEST_ERROR,
  SERVICE_ANIMAL_REQUIRED_Q,
  SERVICE_ANIMAL_TASKS_Q,
  SEND_MY_REQUEST,
  SIGN_HELPER,
  SIGN_HELPER_CONTACTS,
  SIGN_LABEL,
  STILL_NEEDED,
  SUBMITTED_CONFIRMED,
  SUBMITTED_HEADING,
  SUBMITTED_SIGNED,
  SUBMIT_SIGNED,
  VERIFIER_EMAIL_HELPER,
  VERIFIER_EMAIL_LABEL,
  VERIFIER_NAME_LABEL,
  VERIFIER_PHONE_LABEL,
  adultSubjectPermissionLine,
  attestationLine,
  confirmIntro,
  disabilityDefinition,
  disabilityHeading,
  formIntro,
  needAffirmation,
  requestHelper,
  subjectEmailLabel,
  subjectHereChoice,
  subjectLinkChoice,
  subjectSignLabel,
  verifierRoleLabel,
  type RraCopySubject,
} from '@/lib/rraCopy';
import {
  EMPTY_VERIFIER,
  FIELD_ID,
  FORM_UI,
  buildPermissionBody,
  buildSubmissionBody,
  formVoice,
  initialFormState,
  isAdultMember,
  itemHeading,
  missingItems,
  missingPermissionItems,
  needLines,
  verifierDraftsFromProposed,
  type AuthChoice,
  type FormState,
  type ItemFormState,
  type MissingItem,
  type RequestDraft,
  type VerifierDraft,
} from '@/lib/rraRequestDraft';
import { RequestStatementFields } from '@/components/RraRequestFields';
import { HelpBox } from '@/components/rra/HelpBox';
import { PermissionBlock } from '@/components/rra/PermissionBlock';
import { SubjectFields } from '@/components/rra/SubjectFields';

// Clears the fieldset's own border/padding/side margins only: a block margin of 0 would override
// the design system's `.stack > * + *` spacing and butt the group against the field above it.
const groupReset = { border: 0, padding: 0, marginInline: 0, minWidth: 0 } as const;
const legendStyle = { fontWeight: 'var(--weight-medium)', padding: 0, marginBottom: 'var(--space-2)' } as const;
const h2Style = { margin: 0, fontSize: 'var(--text-lg)' } as const;
const mutedSmall = { color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', margin: 0 } as const;

/** Move keyboard focus to a field the "Still needed" list names (a fieldset focuses its first control). */
function focusField(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  const target = el.matches('input,textarea,select,button')
    ? el
    : (el.querySelector('input,textarea,select,button') as HTMLElement | null) ?? el;
  if (!target.hasAttribute('tabindex') && !target.matches('input,textarea,select,button')) {
    target.setAttribute('tabindex', '-1');
  }
  target.scrollIntoView?.({ block: 'center' });
  target.focus?.();
}

function errorMessageOf(e: unknown, fallback: string): string {
  return e instanceof RraFormApiError ? (e.errorMessage ?? fallback) : fallback;
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function RraFormPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [view, setView] = useState<RraFormView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getRraForm(token);
        if (!cancelled) setView(v);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) return <Shell><p>Loading…</p></Shell>;
  if (!view || view.state === 'unavailable')
    return (
      <Shell>
        <h1 className="card-title">This link is no longer available</h1>
        <p>If you believe this is an error, contact our office at {OFFICE_PHONE}.</p>
        {loadError ? <p style={mutedSmall}>{loadError}</p> : null}
      </Shell>
    );

  if (view.state === 'closed')
    return (
      <Shell>
        <h1 className="card-title">This request is closed</h1>
        <p>
          This accommodation request is no longer open. If you'd like to make a new request —
          or think this is a mistake — call our office at {OFFICE_PHONE} or reply to our email.
          You can make a request at any time.
        </p>
      </Shell>
    );

  if (view.state === 'permission_done')
    return (
      <Shell>
        <p style={mutedSmall}>Accommodation request · Welcome Home Properties</p>
        {/* PERMISSION_PAGE.done already opens with "Thank you", so the heading is the page's own. */}
        <h1 className="card-title">{PERMISSION_PAGE.heading}</h1>
        <p>{PERMISSION_PAGE.done}</p>
        <p style={{ ...mutedSmall, marginTop: 'var(--space-4)' }}>Questions? Call our office at {OFFICE_PHONE}.</p>
      </Shell>
    );

  if (view.state === 'permission')
    return (
      <Shell>
        <PermissionPage token={token} view={view} onView={setView} />
      </Shell>
    );

  if (view.state === 'submitted')
    return (
      <Shell>
        <SubmittedPage token={token} view={view} onView={setView} />
      </Shell>
    );

  return (
    <Shell>
      <FillableForm token={token} view={view} onView={setView} />
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// The fillable form
// ---------------------------------------------------------------------------

export function FillableForm(props: {
  token: string;
  view: RraFormView;
  onView: (v: RraFormView) => void;
  /** Test seams: render the form in a given state, with the "Still needed" list already open. */
  initialForm?: FormState;
  initialShowMissing?: boolean;
}) {
  const { token, view, onView } = props;
  const [form, setForm] = useState<FormState>(() => props.initialForm ?? initialFormState(view));
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showMissing, setShowMissing] = useState(props.initialShowMissing ?? false);
  const [attempt, setAttempt] = useState(0);
  const [letterFile, setLetterFile] = useState<File | null>(null);
  const [letterBusy, setLetterBusy] = useState(false);
  const [letterNote, setLetterNote] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const voice = formVoice(form);
  const missing = useMemo(() => missingItems(form), [form]);
  const needed = useMemo(
    () => new Set(showMissing ? missing.map((m) => m.fieldId) : []),
    [showMissing, missing],
  );

  // B10: focus moves to the "Still needed" list when a press finds something missing.
  useEffect(() => {
    if (attempt > 0) listRef.current?.focus();
  }, [attempt]);
  useEffect(() => {
    if (serverError) alertRef.current?.focus();
  }, [serverError]);

  const patch = useCallback((p: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...p }));
    setFieldErrors({});
  }, []);
  const patchItem = useCallback((itemId: string, fn: (i: ItemFormState) => ItemFormState) => {
    setForm((prev) => ({ ...prev, items: prev.items.map((i) => (i.itemId === itemId ? fn(i) : i)) }));
    setFieldErrors({});
  }, []);

  const uploadLetter = useCallback(async () => {
    if (!letterFile) return;
    setLetterBusy(true);
    setLetterNote(null);
    setServerError(null);
    try {
      const res = await uploadRraLetter(token, letterFile);
      if (res.view.state !== 'unavailable') onView(res.view);
      patch({ letterOnFile: true });
      setLetterFile(null);
      setLetterNote('Your letter was received — thank you.');
    } catch (e) {
      setServerError(errorMessageOf(e, 'We could not upload that file. Please try again.'));
    } finally {
      setLetterBusy(false);
    }
  }, [token, letterFile, onView, patch]);

  const submit = useCallback(async () => {
    setBusy(true);
    setServerError(null);
    setFieldErrors({});
    try {
      const res = await submitRraForm(token, buildSubmissionBody(form));
      onView(res.view);
      window.scrollTo?.({ top: 0 });
    } catch (e) {
      if (e instanceof RraFormApiError) setFieldErrors(e.fieldMessages);
      setServerError(errorMessageOf(e, 'Submission failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  }, [token, form, onView]);

  const press = (): void => {
    if (missing.length > 0) {
      setShowMissing(true);
      setAttempt((n) => n + 1);
      return;
    }
    void submit();
  };

  const { shape } = form;
  const need = needLines(form);
  const contacts = shape.askConfirmation && form.authChoice === 'contacts';
  const adult = isAdultMember(form);
  const anyAnimalItem = form.items.some((i) => i.isAnimal);
  const subjectName = form.subjectName.trim();

  return (
    <>
      <p style={mutedSmall}>Accommodation request · Welcome Home Properties</p>
      <h1 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>
        {view.requesterName || 'Your request'}
      </h1>
      <p style={{ color: 'var(--fg-muted)', marginTop: 0 }}>{formIntro(OFFICE_PHONE)}</p>
      <p style={{ color: 'var(--fg-muted)', marginTop: 0 }}>{FORM_PRIVACY_LINE}</p>

      <div className="stack" style={{ gap: 'var(--space-5)' }}>
        {/* --- B2: who is this request for? --- */}
        <section className="stack">
          <SubjectFields
            idPrefix="rra"
            value={{ choice: form.subjectChoice, name: form.subjectName, minor: form.subjectMinor }}
            onChange={(p) =>
              patch({
                ...(p.choice !== undefined ? { subjectChoice: p.choice } : {}),
                ...(p.name !== undefined ? { subjectName: p.name } : {}),
                ...('minor' in p ? { subjectMinor: p.minor } : {}),
              })
            }
            disabled={busy}
            needed={needed}
          />
        </section>

        {/* --- per request: what you're asking → (service animal: the two questions) → how it would help (B3, B4) --- */}
        {form.items.map((item, idx) => (
          <ItemSections
            key={item.itemId}
            form={form}
            item={item}
            idx={idx}
            voice={voice}
            busy={busy}
            needed={needed}
            fieldErrors={fieldErrors}
            patchItem={patchItem}
          />
        ))}

        {/* --- B5: your disability — the definition, then ONE attestation --- */}
        {shape.askDisability ? (
          <section className="stack">
            <h2 style={h2Style}>{disabilityHeading(voice)}</h2>
            <DefinitionText voice={voice} />
            <label className="choice" style={{ display: 'flex', alignItems: 'flex-start' }}>
              <input
                id={FIELD_ID.disability}
                type="checkbox"
                checked={form.affirmDisability}
                disabled={busy}
                {...(needed.has(FIELD_ID.disability) ? { 'aria-invalid': 'true' as const } : {})}
                onChange={(e) => patch({ affirmDisability: e.target.checked })}
                style={{ flex: 'none', marginTop: '0.2em' }}
              />
              <span>{attestationLine(voice)}</span>
            </label>
            {needed.has(FIELD_ID.disability) ? <FieldNeeded /> : null}
          </section>
        ) : null}

        {/* --- B7: your affirmation — only where need is in the gap (service animals carry two questions) --- */}
        {need.animal || need.other ? (
          <fieldset className="stack" style={groupReset}>
            {/* B10: a checkbox group is a <fieldset><legend>. */}
            <legend style={{ ...legendStyle, fontSize: 'var(--text-lg)' }}>{AFFIRMATION_HEADING}</legend>
            <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 'var(--space-3)' }}>
              {need.animal ? (
                <li>
                  <label className="choice" style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <input
                      id={FIELD_ID.needAnimal}
                      type="checkbox"
                      checked={form.affirmNeedAnimal}
                      disabled={busy}
                      {...(needed.has(FIELD_ID.needAnimal) ? { 'aria-invalid': 'true' as const } : {})}
                      onChange={(e) => patch({ affirmNeedAnimal: e.target.checked })}
                      style={{ flex: 'none', marginTop: '0.2em' }}
                    />
                    <span>{needAffirmation(voice, true)}</span>
                  </label>
                  {needed.has(FIELD_ID.needAnimal) ? <FieldNeeded /> : null}
                </li>
              ) : null}
              {need.other ? (
                <li>
                  <label className="choice" style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <input
                      id={FIELD_ID.needOther}
                      type="checkbox"
                      checked={form.affirmNeedOther}
                      disabled={busy}
                      {...(needed.has(FIELD_ID.needOther) ? { 'aria-invalid': 'true' as const } : {})}
                      onChange={(e) => patch({ affirmNeedOther: e.target.checked })}
                      style={{ flex: 'none', marginTop: '0.2em' }}
                    />
                    <span>{needAffirmation(voice, false)}</span>
                  </label>
                  {needed.has(FIELD_ID.needOther) ? <FieldNeeded /> : null}
                </li>
              ) : null}
            </ul>
          </fieldset>
        ) : null}

        {/* --- B8: confirming your request — only when either prong is in the gap --- */}
        {shape.askConfirmation ? (
          <section className="stack">
            <fieldset id={FIELD_ID.confirm} style={groupReset}>
              <legend style={{ ...legendStyle, fontSize: 'var(--text-lg)' }}>{CONFIRM_HEADING}</legend>
              <p style={{ color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>{confirmIntro(voice)}</p>
              <div className="stack" style={{ gap: 'var(--space-2)' }}>
                {(['contacts', 'letter', 'later'] as const).map((c) => (
                  <label key={c} className="choice" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="rra-confirm"
                      checked={form.authChoice === c}
                      disabled={busy}
                      onChange={() => patch({ authChoice: c as AuthChoice })}
                      style={{ flex: 'none', marginTop: '0.2em' }}
                    />
                    <span>{CONFIRM_CHOICE[c]}</span>
                  </label>
                ))}
              </div>
              {needed.has(FIELD_ID.confirm) ? <FieldNeeded /> : null}
            </fieldset>

            {form.authChoice === 'letter' ? (
              <div className="stack" id={FIELD_ID.letter}>
                <p style={mutedSmall}>{LETTER_DIAGNOSIS_OK}</p>
                <span className="label" id="rra-letter-label">{LETTER_UPLOAD_LABEL}</span>
                <LetterUpload
                  labelId="rra-letter-label"
                  file={letterFile}
                  busy={letterBusy}
                  note={letterNote}
                  onSelect={setLetterFile}
                  onUpload={() => void uploadLetter()}
                  existing={view.uploadedLetters}
                />
                {needed.has(FIELD_ID.letter) ? <FieldNeeded /> : null}
              </div>
            ) : null}

            {contacts ? (
              <div className="stack">
                <VerifierFields
                  idPrefix="rra-verifier"
                  voice={voice}
                  verifiers={form.verifiers}
                  disabled={busy}
                  needed={needed}
                  fieldErrors={fieldErrors}
                  onChange={(verifiers) => patch({ verifiers })}
                />

                {adult && subjectName ? (
                  <fieldset id={FIELD_ID.subjectPerm} style={groupReset}>
                    <legend style={legendStyle}>{adultSubjectPermissionLine(subjectName)}</legend>
                    <div className="stack" style={{ gap: 'var(--space-2)' }}>
                      <label className="choice" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="rra-subject-permission"
                          checked={form.subjectPermChoice === 'link'}
                          disabled={busy}
                          onChange={() => patch({ subjectPermChoice: 'link' })}
                          style={{ flex: 'none', marginTop: '0.2em' }}
                        />
                        <span>{subjectLinkChoice(subjectName)}</span>
                      </label>
                      {form.subjectPermChoice === 'link' ? (
                        <div className="field" style={{ marginLeft: 'var(--space-6)' }}>
                          <label htmlFor={FIELD_ID.subjectEmail}>{subjectEmailLabel(subjectName)}</label>
                          <input
                            id={FIELD_ID.subjectEmail}
                            className="input"
                            type="email"
                            maxLength={320}
                            value={form.subjectEmail}
                            disabled={busy}
                            {...(needed.has(FIELD_ID.subjectEmail) || fieldErrors.subjectPermission ? { 'aria-invalid': 'true' as const } : {})}
                            onChange={(e) => patch({ subjectEmail: e.target.value })}
                          />
                          {needed.has(FIELD_ID.subjectEmail) ? <FieldNeeded /> : null}
                          {fieldErrors.subjectPermission ? (
                            <span className="helper" data-state="error" role="alert">{fieldErrors.subjectPermission}</span>
                          ) : null}
                        </div>
                      ) : null}
                      <label className="choice" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="rra-subject-permission"
                          checked={form.subjectPermChoice === 'here'}
                          disabled={busy}
                          onChange={() => patch({ subjectPermChoice: 'here' })}
                          style={{ flex: 'none', marginTop: '0.2em' }}
                        />
                        <span>{subjectHereChoice(subjectName)}</span>
                      </label>
                      {form.subjectPermChoice === 'here' ? (
                        <div className="field" style={{ marginLeft: 'var(--space-6)' }}>
                          <label htmlFor={FIELD_ID.subjectSign}>{subjectSignLabel(subjectName)}</label>
                          <input
                            id={FIELD_ID.subjectSign}
                            className="input"
                            maxLength={200}
                            value={form.subjectSignedName}
                            disabled={busy}
                            {...(needed.has(FIELD_ID.subjectSign) ? { 'aria-invalid': 'true' as const } : {})}
                            onChange={(e) => patch({ subjectSignedName: e.target.value })}
                          />
                          {needed.has(FIELD_ID.subjectSign) ? <FieldNeeded /> : null}
                          <SignaturePreview name={form.subjectSignedName} />
                        </div>
                      ) : null}
                    </div>
                    {needed.has(FIELD_ID.subjectPerm) ? <FieldNeeded /> : null}
                  </fieldset>
                ) : null}

                {/* D1: counsel's text, one box per named person, re-rendering as the name is typed.
                    An adult member who will sign from their own link sees their box on their own page. */}
                {!adult || form.subjectPermChoice === 'here' ? (
                  <PermissionBoxes
                    verifiers={form.verifiers}
                    signer={adult ? 'subject' : voice.minor ? 'parent_guardian' : 'subject'}
                    subjectName={subjectName}
                    needIsAnimal={anyAnimalItem}
                  />
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* --- B10 / B9: signature and submit, or confirm and send --- */}
        <section className="stack">
          {shape.confirmOnly ? (
            <h2 style={h2Style}>{CONFIRM_ONLY_QUESTION}</h2>
          ) : (
            <>
              <div className="field">
                <label htmlFor={FIELD_ID.sign}>{SIGN_LABEL}</label>
                <input
                  id={FIELD_ID.sign}
                  className="input"
                  maxLength={200}
                  value={form.signedName}
                  disabled={busy}
                  autoComplete="name"
                  {...(needed.has(FIELD_ID.sign) ? { 'aria-invalid': 'true' as const } : {})}
                  aria-describedby="rra-sign-helper"
                  onChange={(e) => patch({ signedName: e.target.value })}
                />
                {needed.has(FIELD_ID.sign) ? <FieldNeeded /> : null}
              </div>
              <SignaturePreview name={form.signedName} />
              <p id="rra-sign-helper" style={mutedSmall}>
                {SIGN_HELPER}
                {contacts && !adult ? ` ${SIGN_HELPER_CONTACTS}` : ''}
              </p>
            </>
          )}

          {showMissing && missing.length > 0 ? (
            <StillNeededList ref={listRef} items={missing} />
          ) : null}
          {serverError ? (
            <div ref={alertRef} className="alert" data-tone="danger" role="alert" tabIndex={-1}>
              <div className="alert-body">{serverError}</div>
            </div>
          ) : null}
          <div>
            <button className="btn" data-variant="primary" disabled={busy} onClick={press}>
              {busy ? FORM_UI.submitting : shape.confirmOnly ? SEND_MY_REQUEST : SUBMIT_SIGNED}
            </button>
          </div>
        </section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Per-item sections
// ---------------------------------------------------------------------------

function ItemSections(props: {
  form: FormState;
  item: ItemFormState;
  idx: number;
  voice: RraCopySubject;
  busy: boolean;
  needed: ReadonlySet<string>;
  fieldErrors: Record<string, string>;
  patchItem: (itemId: string, fn: (i: ItemFormState) => ItemFormState) => void;
}) {
  const { form, item, idx, voice, busy, needed, fieldErrors, patchItem } = props;
  // The request group's own sentence is the inline message (B3); a server refusal shows when nothing is flagged here.
  const requestError = needed.has(FIELD_ID.request(item.itemId))
    ? REQUEST_ERROR
    : fieldErrors[`items.${idx}.request`];
  const setDraft = (p: Partial<RequestDraft>): void =>
    patchItem(item.itemId, (i) => ({ ...i, draft: { ...i.draft, ...p } }));
  const isService = item.scope === 'service_animal';

  return (
    <>
      <section className="stack">
        <RequestStatementFields
          idPrefix={`rra-item-${item.itemId}`}
          fieldId={FIELD_ID.request(item.itemId)}
          heading={itemHeading(form, idx)}
          helper={requestHelper(item.isAnimal)}
          value={item.draft}
          subject={voice}
          isAnimal={item.isAnimal}
          disabled={busy}
          onChange={setDraft}
          error={requestError}
        />
      </section>

      {/* The two permitted questions: a service animal with need in the gap only (ESA items never show them).
          No animal facts are asked here (RRA v2.2) — the questions stand on their own. */}
      {isService && item.inNeedGap ? (
        <section className="stack">
          <fieldset style={groupReset}>
            <legend style={legendStyle}>{SERVICE_ANIMAL_REQUIRED_Q}</legend>
            <div className="cluster" style={{ gap: 'var(--space-5)' }}>
              <label className="choice" style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={`rra-item-${item.itemId}-required`}
                  checked={item.requiredBecauseOfDisability === false}
                  disabled={busy}
                  onChange={() => patchItem(item.itemId, (i) => ({ ...i, requiredBecauseOfDisability: false }))}
                />{' '}
                No
              </label>
              <label className="choice" style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={`rra-item-${item.itemId}-required`}
                  checked={item.requiredBecauseOfDisability === true}
                  disabled={busy}
                  onChange={() => patchItem(item.itemId, (i) => ({ ...i, requiredBecauseOfDisability: true }))}
                />{' '}
                Yes
              </label>
            </div>
          </fieldset>
          <div className="field">
            <label htmlFor={`rra-item-${item.itemId}-support`}>{SERVICE_ANIMAL_TASKS_Q}</label>
            <textarea
              id={`rra-item-${item.itemId}-support`}
              className="input"
              rows={3}
              maxLength={1000}
              value={item.supportProvided}
              disabled={busy}
              onChange={(e) => patchItem(item.itemId, (i) => ({ ...i, supportProvided: e.target.value }))}
            />
          </div>
        </section>
      ) : null}

      {/* B4: the one optional help box — need in the gap, never on a service-animal item. */}
      {item.inNeedGap && !isService ? (
        <section className="stack">
          <HelpBox
            idPrefix={`rra-item-${item.itemId}`}
            value={item.draft.howChangeHelps}
            onChange={(v) => setDraft({ howChangeHelps: v })}
            subject={voice}
            animal={item.isAnimal}
            disabled={busy}
            error={fieldErrors[`items.${idx}.howChangeHelps`]}
          />
        </section>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

function FieldNeeded() {
  return (
    <p role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', margin: 'var(--space-1) 0 0' }}>
      {FORM_UI.fieldNeeded}
    </p>
  );
}

/** B5: the Washington definition, in NORMAL text color — intro, three bullets, tail. */
function DefinitionText({ voice }: { voice: RraCopySubject }) {
  const d = disabilityDefinition(voice);
  return (
    <div
      style={{
        background: 'var(--bg-alt)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        color: 'var(--fg)',
      }}
    >
      <p style={{ margin: '0 0 var(--space-2)' }}>{d.intro}</p>
      {/* The design system's base styles strip list markers; the definition's three bullets keep theirs. */}
      <ul style={{ margin: '0 0 var(--space-2)', paddingLeft: 'var(--space-5)', listStyle: 'disc' }}>
        {d.bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
      <p style={{ margin: 0 }}>{d.tail}</p>
    </div>
  );
}

/** The typed name IS the signature — show it as one while they type, the way it will appear signed. */
function SignaturePreview({ name }: { name: string }) {
  if (!name.trim()) return null;
  return (
    <div className="vd-signature" aria-hidden="true">
      <span className="vd-signature-name">{name.trim()}</span>
      <span className="vd-signature-meta">Your electronic signature</span>
    </div>
  );
}

/** B10: "Almost there. Still needed:" and a linked list naming each missing item by its label. */
export const StillNeededList = ({ items, ref }: { items: MissingItem[]; ref: React.Ref<HTMLDivElement> }) => (
  <div
    ref={ref}
    tabIndex={-1}
    role="alert"
    className="alert"
    data-tone="warning"
    style={{ scrollMarginTop: 'var(--space-6)' }}
  >
    <div className="alert-body">
      <div className="alert-title">{STILL_NEEDED}</div>
      <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', listStyle: 'disc' }}>
        {items.map((m) => (
          <li key={m.fieldId}>
            {/* A linked list (B10): underlined so it reads as links, not as plain text. */}
            <a
              href={`#${m.fieldId}`}
              style={{ textDecoration: 'underline' }}
              onClick={(e) => {
                e.preventDefault();
                focusField(m.fieldId);
              }}
            >
              {m.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/** D1: one box per named person (a placeholder box until someone is named). */
function PermissionBoxes(props: {
  verifiers: VerifierDraft[];
  signer: 'subject' | 'parent_guardian';
  subjectName: string;
  needIsAnimal: boolean;
}) {
  const named = props.verifiers.filter((v) => v.name.trim());
  const names = named.length > 0 ? named.map((v) => v.name.trim()) : [''];
  return (
    <div className="stack">
      {names.map((n, i) => (
        <PermissionBlock
          key={`${i}-${n}`}
          verifierName={n}
          signer={props.signer}
          subjectName={props.subjectName}
          needIsAnimal={props.needIsAnimal}
          officePhone={OFFICE_PHONE}
        />
      ))}
    </div>
  );
}

/** B8: the people we may contact — up to three — each with labelled inputs. */
function VerifierFields(props: {
  idPrefix: string;
  voice: RraCopySubject;
  verifiers: VerifierDraft[];
  disabled: boolean;
  needed: ReadonlySet<string>;
  fieldErrors: Record<string, string>;
  onChange: (next: VerifierDraft[]) => void;
}) {
  const { verifiers, onChange, disabled } = props;
  const set = (i: number, p: Partial<VerifierDraft>): void =>
    onChange(verifiers.map((v, j) => (j === i ? { ...v, ...p } : v)));
  return (
    <div className="stack">
      {verifiers.map((v, i) => {
        const nameId = i === 0 ? FIELD_ID.verifierName(0) : `${props.idPrefix}-${i}-name`;
        const emailError = props.fieldErrors[`verifiers.${i}.email`];
        return (
          <div key={i} className="stack" style={{ gap: 'var(--space-2)' }}>
            <div className="field">
              <label htmlFor={nameId}>{VERIFIER_NAME_LABEL}</label>
              <input
                id={nameId}
                className="input"
                maxLength={200}
                value={v.name}
                disabled={disabled}
                {...(i === 0 && props.needed.has(FIELD_ID.verifierName(0)) ? { 'aria-invalid': 'true' as const } : {})}
                onChange={(e) => set(i, { name: e.target.value })}
              />
              {i === 0 && props.needed.has(FIELD_ID.verifierName(0)) ? <FieldNeeded /> : null}
            </div>
            <div className="field">
              <label htmlFor={`${props.idPrefix}-${i}-role`}>{verifierRoleLabel(props.voice)}</label>
              <input
                id={`${props.idPrefix}-${i}-role`}
                className="input"
                maxLength={200}
                value={v.role}
                disabled={disabled}
                onChange={(e) => set(i, { role: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`${props.idPrefix}-${i}-email`}>{VERIFIER_EMAIL_LABEL}</label>
              <span className="helper" id={`${props.idPrefix}-${i}-email-helper`}>{VERIFIER_EMAIL_HELPER}</span>
              <input
                id={`${props.idPrefix}-${i}-email`}
                className="input"
                type="email"
                maxLength={320}
                value={v.email}
                disabled={disabled}
                aria-describedby={`${props.idPrefix}-${i}-email-helper`}
                {...(emailError ? { 'aria-invalid': 'true' as const } : {})}
                onChange={(e) => set(i, { email: e.target.value })}
              />
              {emailError ? (
                <span className="helper" data-state="error" role="alert">{emailError}</span>
              ) : null}
            </div>
            <div className="field">
              <label htmlFor={`${props.idPrefix}-${i}-phone`}>{VERIFIER_PHONE_LABEL}</label>
              <input
                id={`${props.idPrefix}-${i}-phone`}
                className="input"
                maxLength={50}
                value={v.phone}
                disabled={disabled}
                onChange={(e) => set(i, { phone: e.target.value })}
              />
            </div>
          </div>
        );
      })}
      {verifiers.length < 3 ? (
        <div>
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            disabled={disabled}
            onClick={() => onChange([...verifiers, { ...EMPTY_VERIFIER }])}
          >
            {ADD_PERSON}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The permission page (A6 / B2) — the requester's, or the adult household member's own
// ---------------------------------------------------------------------------

export function PermissionPage(props: {
  token: string;
  view: RraFormView;
  onView: (v: RraFormView) => void;
  /** Test seam: render the page with a path already chosen. */
  initialChoice?: 'contacts' | 'letter';
}) {
  const { token, view, onView } = props;
  const p = view.permission!;
  const [choice, setChoice] = useState<'contacts' | 'letter' | ''>(props.initialChoice ?? '');
  const [verifiers, setVerifiers] = useState<VerifierDraft[]>(() => verifierDraftsFromProposed(p.proposedVerifiers));
  const [signedName, setSignedName] = useState('');
  const [letterFile, setLetterFile] = useState<File | null>(null);
  const [letterBusy, setLetterBusy] = useState(false);
  const [letterNote, setLetterNote] = useState<string | null>(null);
  const [uploadedHere, setUploadedHere] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showMissing, setShowMissing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  // The voice: the adult member signs their own permission in the first person; a parent signs for a
  // minor with the child named.
  const voice: RraCopySubject = p.signerIsGuardian
    ? { readerIsSubject: false, name: p.subjectName, minor: true }
    : { readerIsSubject: true, name: '', minor: false };

  const letterSent =
    uploadedHere || view.uploadedLetters.some((l) => l.uploadedAt >= p.askedAt);
  const state = { choice, verifiers, signedName, letterSent };
  const missing = useMemo(
    () => missingPermissionItems({ choice, verifiers, signedName, letterSent }),
    [choice, verifiers, signedName, letterSent],
  );
  const needed = useMemo(() => new Set(showMissing ? missing.map((m) => m.fieldId) : []), [showMissing, missing]);

  useEffect(() => {
    if (attempt > 0) listRef.current?.focus();
  }, [attempt]);
  useEffect(() => {
    if (serverError) alertRef.current?.focus();
  }, [serverError]);

  const upload = async (): Promise<void> => {
    if (!letterFile) return;
    setLetterBusy(true);
    setLetterNote(null);
    setServerError(null);
    try {
      const res = await uploadRraLetter(token, letterFile);
      if (res.view.state !== 'unavailable') onView(res.view);
      setUploadedHere(true);
      setLetterFile(null);
      setLetterNote('Your letter was received — thank you.');
    } catch (e) {
      setServerError(errorMessageOf(e, 'We could not upload that file. Please try again.'));
    } finally {
      setLetterBusy(false);
    }
  };

  const submit = async (): Promise<void> => {
    setBusy(true);
    setServerError(null);
    setFieldErrors({});
    try {
      const res = await submitRraPermission(token, buildPermissionBody(state));
      onView(res.view);
      window.scrollTo?.({ top: 0 });
    } catch (e) {
      if (e instanceof RraFormApiError) setFieldErrors(e.fieldMessages);
      setServerError(errorMessageOf(e, 'Submission failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const press = (): void => {
    if (missing.length > 0) {
      setShowMissing(true);
      setAttempt((n) => n + 1);
      return;
    }
    void submit();
  };

  return (
    <>
      <p style={mutedSmall}>Accommodation request · Welcome Home Properties</p>
      <h1 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>{PERMISSION_PAGE.heading}</h1>
      <p style={{ color: 'var(--fg-muted)', marginTop: 0 }}>{PERMISSION_PAGE.intro}</p>

      <div className="stack" style={{ gap: 'var(--space-5)' }}>
        <fieldset id={FIELD_ID.confirm} style={groupReset}>
          <legend style={legendStyle}>{CONFIRM_HEADING}</legend>
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            {(['contacts', 'letter'] as const).map((c) => (
              <label key={c} className="choice" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="rra-permission-choice"
                  checked={choice === c}
                  disabled={busy}
                  onChange={() => setChoice(c)}
                  style={{ flex: 'none', marginTop: '0.2em' }}
                />
                <span>{CONFIRM_CHOICE[c]}</span>
              </label>
            ))}
          </div>
          {needed.has(FIELD_ID.confirm) ? <FieldNeeded /> : null}
        </fieldset>

        {choice === 'contacts' ? (
          <div className="stack">
            <VerifierFields
              idPrefix="rra-permission-verifier"
              voice={voice}
              verifiers={verifiers}
              disabled={busy}
              needed={needed}
              fieldErrors={fieldErrors}
              onChange={setVerifiers}
            />
            <PermissionBoxes
              verifiers={verifiers}
              signer={p.signerIsGuardian ? 'parent_guardian' : 'subject'}
              subjectName={p.subjectName}
              needIsAnimal={p.needIsAnimal}
            />
            <div className="field">
              <label htmlFor={FIELD_ID.sign}>{SIGN_LABEL}</label>
              <input
                id={FIELD_ID.sign}
                className="input"
                maxLength={200}
                value={signedName}
                disabled={busy}
                autoComplete="name"
                aria-describedby="rra-permission-sign-helper"
                {...(needed.has(FIELD_ID.sign) ? { 'aria-invalid': 'true' as const } : {})}
                onChange={(e) => setSignedName(e.target.value)}
              />
              {needed.has(FIELD_ID.sign) ? <FieldNeeded /> : null}
            </div>
            <SignaturePreview name={signedName} />
            <p id="rra-permission-sign-helper" style={mutedSmall}>{PERMISSION_PAGE.signHelper}</p>
          </div>
        ) : null}

        {choice === 'letter' ? (
          <div className="stack" id={FIELD_ID.letter}>
            <p style={mutedSmall}>{LETTER_DIAGNOSIS_OK}</p>
            <span className="label" id="rra-permission-letter-label">{LETTER_UPLOAD_LABEL}</span>
            <LetterUpload
              labelId="rra-permission-letter-label"
              file={letterFile}
              busy={letterBusy}
              note={letterNote}
              onSelect={setLetterFile}
              onUpload={() => void upload()}
              existing={view.uploadedLetters}
            />
            {needed.has(FIELD_ID.letter) ? <FieldNeeded /> : null}
          </div>
        ) : null}

        {showMissing && missing.length > 0 ? <StillNeededList ref={listRef} items={missing} /> : null}
        {serverError ? (
          <div ref={alertRef} className="alert" data-tone="danger" role="alert" tabIndex={-1}>
            <div className="alert-body">{serverError}</div>
          </div>
        ) : null}
        <div>
          <button className="btn" data-variant="primary" disabled={busy} onClick={press}>
            {busy ? FORM_UI.submitting : choice === 'letter' ? PERMISSION_PAGE.sendLetter : PERMISSION_PAGE.submit}
          </button>
        </div>
        <p style={mutedSmall}>Questions? Call our office at {OFFICE_PHONE}.</p>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// The submitted page (B11)
// ---------------------------------------------------------------------------

export function SubmittedPage(props: { token: string; view: RraFormView; onView: (v: RraFormView) => void }) {
  const { token, view, onView } = props;
  const [letterFile, setLetterFile] = useState<File | null>(null);
  const [letterBusy, setLetterBusy] = useState(false);
  const [letterNote, setLetterNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sub = view.submitted;
  // A pre-v2.1 record has no `submitted` block: treat it as the signed path.
  const signed = sub ? sub.signed : true;

  const upload = async (): Promise<void> => {
    if (!letterFile) return;
    setLetterBusy(true);
    setLetterNote(null);
    setError(null);
    try {
      const res = await uploadRraLetter(token, letterFile);
      if (res.view.state !== 'unavailable') onView(res.view);
      setLetterFile(null);
      setLetterNote('Your letter was received — thank you.');
    } catch (e) {
      setError(errorMessageOf(e, 'We could not upload that file. Please try again.'));
    } finally {
      setLetterBusy(false);
    }
  };

  return (
    <>
      <h1 className="card-title">{SUBMITTED_HEADING}</h1>
      <p>{signed ? SUBMITTED_SIGNED : SUBMITTED_CONFIRMED}</p>
      {(sub?.download ?? view.signedRequest) ? (
        <p>
          <a className="btn" data-variant="secondary" href={rraRequestPdfUrl(token)} download>
            {signed ? DOWNLOAD_SIGNED_COPY : DOWNLOAD_CONFIRMED_COPY}
          </a>
        </p>
      ) : null}
      {sub?.nextStep ? <p>{sub.nextStep}</p> : null}
      {view.uploadedLetters.length > 0 ? (
        <p style={{ color: 'var(--fg-muted)' }}>On file: {view.uploadedLetters.map((l) => l.filename).join(', ')}</p>
      ) : null}
      {/* A letter is invited only where there is still something to confirm — an apparent request asks for nothing more. */}
      {view.shape.askConfirmation ? (
        <div className="stack" style={{ marginTop: 'var(--space-4)' }}>
          <span className="label" id="rra-submitted-letter-label">
            Have a letter you'd like to add? You can upload it here.
          </span>
          {error ? <div className="alert" data-tone="danger" role="alert"><div className="alert-body">{error}</div></div> : null}
          <LetterUpload
            labelId="rra-submitted-letter-label"
            file={letterFile}
            busy={letterBusy}
            note={letterNote}
            onSelect={setLetterFile}
            onUpload={() => void upload()}
          />
        </div>
      ) : null}
      <p style={{ ...mutedSmall, marginTop: 'var(--space-4)' }}>Questions? Call our office at {OFFICE_PHONE}.</p>
    </>
  );
}

/** Letter upload control (WEL-117). The prominent "Upload letter" action stays disabled until a file is
 *  chosen: a hidden <input> driven by a styled "Choose PDF" button and a drag-and-drop zone. */
function LetterUpload(props: {
  labelId: string;
  file: File | null;
  busy: boolean;
  note: string | null;
  onSelect: (f: File | null) => void;
  onUpload: () => void;
  existing?: Array<{ filename: string }>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const openPicker = () => inputRef.current?.click();
  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        aria-labelledby={props.labelId}
        style={{ display: 'none' }}
        onChange={(e) => {
          props.onSelect(e.target.files?.[0] ?? null);
          // Allow re-selecting the same file later.
          e.target.value = '';
        }}
      />
      <div
        role="button"
        tabIndex={0}
        aria-labelledby={props.labelId}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          props.onSelect(e.dataTransfer.files?.[0] ?? null);
        }}
        style={{
          border: `1px dashed var(--border)`,
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-4)',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'var(--bg-alt)' : 'transparent',
        }}
      >
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            openPicker();
          }}
        >
          Choose PDF
        </button>
        <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
          {props.file ? props.file.name : 'or drag a PDF here'}
        </p>
      </div>
      {props.note && <p style={{ color: 'var(--fg-muted)', margin: 0 }}>{props.note}</p>}
      {props.existing && props.existing.length > 0 && (
        <p style={{ color: 'var(--fg-muted)', margin: 0 }}>
          On file: {props.existing.map((l) => l.filename).join(', ')}
        </p>
      )}
      <button
        type="button"
        className="btn"
        data-variant="primary"
        disabled={!props.file || props.busy}
        onClick={props.onUpload}
      >
        {props.busy ? 'Uploading…' : 'Upload letter'}
      </button>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--fg)', fontFamily: 'var(--font-sans)', padding: 'var(--space-6)' }}>
      <div className="card" style={{ maxWidth: 640, margin: '0 auto', padding: 'var(--space-6)' }}>{children}</div>
    </div>
  );
}
